import React, {useMemo} from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';
import {useParams} from 'react-router-dom';
import {formatISODate} from '../../shared/helpers';
import {parseHtml} from './helpers';

// components
import {CardBorder, CardError, PageTemplate} from '../../shared/components';
import {LoadingPage} from '../LoadingPage';

// styles
import {
  ArticleContent,
  ArticleDetailsPageWrapper,
  ArticleVideo,
} from './ArticleDetailsPage-styles';

// types
import {
  ArticleDetails,
  CreditBookApiError,
} from '../../shared/services/ApiService/types';

/**
 * Fetch the article details
 * @param articleId
 */
const fetchArticleDetails = async (articleId: string | undefined) => {
  if (!articleId) {
    throw new Error('Article ID is required');
  }
  const {data} = await CreditBookApi.article.getArticleDetails(articleId);
  return data;
};

const ArticleDetailsPage = () => {
  const {t, i18n} = useTranslation();
  const {articleId} = useParams();

  const {
    data: articleDetails = null,
    isLoading: articleDetailsLoading,
    error: articleDetailsError,
    isRefetching: isArticleDetailsRefetching,
  } = useQuery<ArticleDetails, CreditBookApiError>({
    queryKey: ['getArticleDetails'],
    queryFn: () => fetchArticleDetails(articleId),
  });

  /**
   * Render the article details based on the states
   */
  const renderArticle = () => {
    if (articleDetailsLoading || isArticleDetailsRefetching) {
      return <LoadingPage usePortalConfig />;
    }

    if (articleDetailsError || articleDetails == null) {
      return (
        <CardError
          title={t('article_contentLibrary')}
          message={articleDetailsError?.response?.data?.errorMessage}
        />
      );
    }

    return (
      <CardBorder variant="grey">
        <ArticleDetailsPageWrapper
          gap={articleDetails.videoIframeUrl ? '24px' : '0px'}>
          {articleDetails.videoIframeUrl != null && (
            <ArticleVideo
              src={articleDetails.videoIframeUrl}
              allow="autoplay; fullscreen; picture-in-picture"
              title={articleDetails.title}
            />
          )}
          {articleDetails.htmlContent != null && (
            <ArticleContent>
              {parseHtml(articleDetails.htmlContent)}
            </ArticleContent>
          )}
        </ArticleDetailsPageWrapper>
      </CardBorder>
    );
  };

  const pageTitle = useMemo(() => {
    if (articleDetails == null) {
      return;
    }
    return articleDetails.title;
  }, [articleDetails]);

  const pageSubtitle = useMemo(() => {
    if (articleDetails == null) {
      return;
    }

    const publishedDate = formatISODate(
      articleDetails.createdAt,
      i18n.language === 'en' ? 'en' : 'fr',
    );

    return t('article_publishedOn', {date: publishedDate});
  }, [articleDetails, i18n.language, t]);

  return (
    <PageTemplate title={pageTitle} subtitle={pageSubtitle}>
      {renderArticle()}
    </PageTemplate>
  );
};

export default ArticleDetailsPage;
